import { css, cx } from '@emotion/css';
import { Colors } from '../styles/colors';
import { button1Style } from '../styles/text';
import { Motion, Radius } from '../styles/tokens';

interface ITabSwitchProps {
  tabs: string[];
  selectedIndex: number;
  setIndex(index: number): void;
  className?: string;
}

/** Segmented control: same tabs, same order, same position as before. */
export const TabSwitch = (props: ITabSwitchProps): JSX.Element => {
  const { tabs, selectedIndex, setIndex, className } = props;

  return (
    <div className={cx(tabSwitchStyle, className)} role="tablist">
      {tabs.map((tab, i) => {
        const active = selectedIndex === i;
        return (
          <button
            key={i}
            type="button"
            role="tab"
            aria-selected={active}
            className={cx(button1Style, tabStyle, active && activeTabStyle)}
            onClick={() => setIndex(i)}
          >
            {tab}
          </button>
        );
      })}
    </div>
  );
};

const tabSwitchStyle = css`
  display: flex;
  width: 100%;
  padding: 0.25rem;
  gap: 0.25rem;
  background-color: ${Colors.SURFACE_MUTED};
  border-radius: ${Radius.md};
`;

const tabStyle = css`
  appearance: none;
  border: 0;
  background: transparent;
  padding: 0.625rem 0.75rem;
  border-radius: calc(${Radius.md} - 0.25rem);

  flex-grow: 1;
  flex-basis: 0;
  min-width: 0;
  font-size: 1rem;
  line-height: 1.25rem;
  color: ${Colors.CAPRI_BLUE};

  cursor: pointer;
  text-transform: capitalize;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition:
    background-color ${Motion.fast},
    color ${Motion.fast},
    box-shadow ${Motion.fast};

  @media (hover: hover) {
    &:hover {
      background-color: rgba(255, 255, 255, 0.6);
    }
  }

  &:focus-visible {
    outline: 2px solid ${Colors.CAPRI_BLUE};
    outline-offset: -2px;
  }
`;

const activeTabStyle = css`
  color: ${Colors.WHITE};
  background-color: ${Colors.CAPRI_BLUE};
  box-shadow: 0 1px 2px rgba(16, 42, 67, 0.2);

  @media (hover: hover) {
    &:hover {
      background-color: ${Colors.CAPRI_BLUE};
    }
  }
`;
